import { useEffect } from "react";
import styled from "styled-components";
import { Flex, Text } from "rebass/styled-components";

import { analytics } from "MVVM/Analytics";
import { Button } from "MVVM/Components/Button";
import { luminance } from "MVVM/Utilities/Styling";
import { useUser } from "MVVM/Hooks/useUser";

import { Bind } from "./Bind";
import { Faq } from "./Faq";
import { Feed } from "./Feed";
import { FINAL_CTA } from "./data";
import { Hero } from "./Hero";
import { HowItWorks } from "./HowItWorks";
import { Plans } from "./Plans";
import {
  ACCENT_BORDER,
  ACCENT_DEEP,
  ACCENT_SOFT,
  ContrastBand,
  HeroBackground,
  PageShell,
  Section,
  SectionHeading,
  SectionSubheading,
  Underlined,
} from "../styledComponents";

const AllstarCommand = () => {
  const { allstarUser } = useUser();
  const loggedIn = Boolean(allstarUser?.loggedIn);

  // ponytail: client-side only. The repo has no per-page meta system, so OG
  // tags still come from public/index.html.
  useEffect(() => {
    document.title = "!allstar | Allstar";
  }, []);

  return (
    <PageShell>
      <Hero />

      {/* The bind is the one action a visitor can take straight off the hero,
          so it sits directly under it rather than after the explainer. */}
      <ContrastBand>
        <Bind />
      </ContrastBand>

      <HowItWorks />

      <ContrastBand>
        <Feed />
      </ContrastBand>

      <Plans />

      <ContrastBand>
        <Faq />
      </ContrastBand>

      <HeroBackground>
        <Section py={[80, 100, 120]} alignItems="center">
          <SectionHeading textAlign="center">
            {FINAL_CTA.headingLead}
            <Underlined>{FINAL_CTA.headingUnderlined}</Underlined>
          </SectionHeading>
          <SectionSubheading textAlign="center">
            {FINAL_CTA.subheading}
          </SectionSubheading>

          {/* Logged-in visitors already have everything they need: the command
              works in their next match, so the only useful link is their clips. */}
          <Flex mt={8} justifyContent="center">
            <AccentButton
              classification="roundedFilled"
              to={loggedIn ? "/library" : "/signup"}
              onClick={() =>
                analytics.track("Button - Click", {
                  location: "allstarCommandLandingPage",
                  locationIdentifier: "finalCta",
                  source: "website",
                })
              }
            >
              <Text fontSize={17} fontWeight={700}>
                {loggedIn ? "See your clips" : "Get started for free"}
              </Text>
            </AccentButton>
          </Flex>
        </Section>
      </HeroBackground>
    </PageShell>
  );
};

const BUTTON_GRADIENT = `linear-gradient(135deg, ${ACCENT_DEEP} 0%, ${luminance(
  ACCENT_DEEP,
  -0.42,
)} 100%)`;

const AccentButton = styled(Button)`
  background: ${BUTTON_GRADIENT};
  border: 1px solid ${ACCENT_BORDER};
  border-radius: 10px;
  box-shadow: 0 8px 30px ${ACCENT_SOFT};
  color: ${({ theme }) => theme.colors.ice};
  max-width: max-content;
  padding: 15px 28px;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;

  :hover {
    background: ${BUTTON_GRADIENT};
    box-shadow: 0 10px 40px ${ACCENT_BORDER};
    color: ${({ theme }) => theme.colors.ice};
    transform: translateY(-2px);
  }
`;

export default AllstarCommand;
